import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import type { TouchpointTopAnswerComponent } from '@/lib/api/types';
import { fmtInt } from '@/features/insights/engine';
import { useScrollRamp } from '@/features/insights/motion/useScrollRamp';

/**
 * Las respuestas más frecuentes de la estación: una píldora por pregunta y,
 * para la elegida, una fila por opción con su barra. La opción más votada va
 * en lima y el resto en oliva: una sola magnitud, un solo tono. Las barras
 * crecen con la rampa.
 */
export function StationTopAnswers({ components, className }: { components: TouchpointTopAnswerComponent[]; className?: string }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const [activeId, setActiveId] = useState<number | null>(components[0]?.componentId ?? null);

  if (components.length === 0) {
    return (
      <section className={cn('rounded-[14px] border p-6 text-center text-sm', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)', color: 'var(--color-dash-fg-muted)' }}>
        {t('stations.detail.dashboardEmpty')}
      </section>
    );
  }

  const active = components.find((c) => c.componentId === activeId) ?? components[0]!;
  const opciones = [...active.options].sort((a, b) => b.count - a.count);
  const total = opciones.reduce((s, o) => s + o.count, 0) || 1;
  const max = Math.max(1, ...opciones.map((o) => o.count));

  return (
    <section ref={ref} className={cn('flex flex-col gap-4 rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-[22px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{t('stations.detail.topAnswers')}</h3>
        <span className="text-[13px]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('stations.dash.answered', { n: fmtInt(total) })}</span>
      </div>

      {components.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {components.map((c) => {
            const on = c.componentId === active.componentId;
            return (
              <button
                key={c.componentId}
                type="button"
                onClick={() => setActiveId(c.componentId)}
                className="rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors"
                style={on ? { background: 'var(--color-dash-ink)', color: 'var(--color-dash-lime)' } : { background: 'var(--color-dash-paper-3)', color: 'var(--color-dash-fg-2)' }}
              >
                {c.componentLabel}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {opciones.map((o, i) => (
          <div key={o.value} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-3 text-[13.5px]">
              <span className="min-w-0 flex-1 truncate font-semibold" style={{ color: 'var(--color-dash-fg)' }} title={o.value}>
                {o.value}
              </span>
              <span className="dash-mono whitespace-nowrap text-xs font-bold tabular-nums" style={{ color: 'var(--color-dash-fg)' }}>
                {fmtInt(Math.round(o.count * p))}{' '}
                <span className="font-normal" style={{ color: 'var(--color-dash-fg-muted)' }}>({Math.round((o.count / total) * 100 * p)}%)</span>
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded" style={{ background: 'var(--color-dash-track)' }}>
              <div
                className="h-full rounded transition-[width] duration-[650ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]"
                style={{ width: `${(o.count / max) * 100 * p}%`, background: i === 0 ? 'var(--color-dash-lime)' : 'var(--color-dash-olive)' }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
